/**
 * A Recents row's snooze action: a clock that opens the snooze presets.
 *
 * @module components/home/RecentThreadSnoozeButton
 */
import { ClockIcon } from "lucide-react";
import { useMemo } from "react";

import { requestCustomSnooze } from "../customSnoozeRequest";
import { resolveSnoozePresets, type SnoozePreset } from "../Sidebar.snooze";
import { Popover, PopoverPopup, PopoverTrigger } from "../ui/popover";
import { Tip } from "../ui/tip";

export function RecentThreadSnoozeButton({
  open,
  onOpenChange,
  onSnooze,
  timestampFormat,
}: {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly onSnooze: (preset: Pick<SnoozePreset, "snoozedUntil">) => void;
  readonly timestampFormat: Parameters<typeof resolveSnoozePresets>[1];
}) {
  const presets = useMemo(() => (open ? resolveSnoozePresets(new Date(), timestampFormat) : []), [open, timestampFormat]);
  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <Tip label="Snooze">
        <PopoverTrigger
          render={
            <button
              type="button"
              aria-label="Snooze thread"
              onClick={(event) => event.stopPropagation()}
              onDoubleClick={(event) => event.stopPropagation()}
              className="inline-flex h-full cursor-pointer items-center gap-1 rounded-md bg-transparent px-1.5 text-xs text-muted-foreground hover:text-foreground"
            />
          }
        >
          <ClockIcon strokeWidth={1.75} className="size-3.5" />
        </PopoverTrigger>
      </Tip>
      <PopoverPopup side="bottom" align="end" className="w-56" viewportClassName="p-1">
        {presets.map((preset) => (
          <button
            key={preset.id}
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              onOpenChange(false);
              onSnooze(preset);
            }}
            className="flex w-full cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-left text-xs text-foreground/90 hover:bg-accent hover:text-foreground"
          >
            <span className="flex-1">{preset.label}</span>
            <span className="font-mono text-[10px] text-muted-foreground/60 tabular-nums">{preset.whenLabel}</span>
          </button>
        ))}
        <div className="my-1 border-t border-border/60" />
        <button
          type="button"
          className="flex w-full cursor-pointer rounded-md px-2 py-1.5 text-left text-xs text-foreground/90 hover:bg-accent hover:text-foreground"
          onClick={async (event) => {
            event.stopPropagation();
            onOpenChange(false);
            const choice = await requestCustomSnooze();
            if (choice) onSnooze(choice);
          }}
        >
          Custom…
        </button>
      </PopoverPopup>
    </Popover>
  );
}
